// Copyright (c) Docugami, Inc. All rights reserved.

import { TooltipHost } from "@fluentui/react";
import { appContentColor } from "components/shell/presentation/AppStyles";
import { dgIconNames, useDgIcons } from "hooks/dgIcons";
import { useAppSelector } from "hooks/redux";
import { createOperationId, useBulkClearBindingsMutation } from "store/reports/reportsSlice";
import { createSelectUnconfirmedBindingIds, selectReportId } from "store/reports/selectors";
import styled from "styled-components";
import { useMemo } from "react";
import { AppState } from "store";
import { DisabledCss } from "components/common/DgCommonStyledComponents";

const IconButtonRoot = styled.button<{ disabled?: boolean }>`
    background: none;
    border: none;
    cursor: pointer;
    color: ${appContentColor};

    :hover {
        transform: scale(1.02);
    }

    ${props => !!props.disabled && DisabledCss};
`

const ButtonLabel = styled.span`
    display: flex;
    align-items: baseline;
    font-size: 10px;
    gap: 5px;
`;

interface ClearBindingsButtonProps {
    entryId: string;
    folderId: string;
    showLabel?: boolean;
    className?: string;
}

export const ClearUnconfirmedBindingsButton = (props: ClearBindingsButtonProps): JSX.Element | null => {
    const reportId = useAppSelector(selectReportId);
    const unconfirmedBindingIdsSelector = useMemo(() => {
        const selector = createSelectUnconfirmedBindingIds()
        return (state: AppState) => selector(state, reportId, props.folderId, props.entryId);
    }, [reportId, props.folderId, props.entryId]);

    const targetBindingIds = useAppSelector(unconfirmedBindingIdsSelector);

    const [trigger, data] = useBulkClearBindingsMutation();

    const { icon } = useDgIcons();

    if (!targetBindingIds?.length) {
        return null;
    }

    async function onClear() {
        if (!targetBindingIds?.length) {
            return;
        }

        await trigger({
            reportId: reportId,
            entryId: props.entryId,
            folderId: props.folderId,
            spanIds: targetBindingIds,
            operationId: createOperationId()
        })
    }

    const tooltipContent = "Remove all unconfirmed chunks from this card";
    return (
        <IconButtonRoot className={props.className} disabled={data.isLoading} onClick={onClear}>
            <TooltipHost content={tooltipContent} styles={{ root: { display: 'flex', alignItems: 'baseline' } }}>
                <ButtonLabel>CLEAR UNCONFIRMED {icon(dgIconNames.DgClearIcon, 'Clear unconfirmed chunks', 11, 11, appContentColor, '')}</ButtonLabel>
            </TooltipHost>
        </IconButtonRoot>
    )
}
